import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage, Language } from '../../contexts/LanguageContext';
import { ChevronDown } from 'lucide-react';

/**
 * Language switcher component with flag icons
 * Allows users to switch between French and English
 */

const LanguageSwitcherContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const CurrentLanguage = styled(motion.button)`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
  padding: ${props => props.theme.spacing[2]} ${props => props.theme.spacing[3]};
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: ${props => props.theme.borderRadius.lg};
  color: #333;
  cursor: pointer;
  transition: all ${props => props.theme.animations.duration.fast} ${props => props.theme.animations.easing.smooth};
  backdrop-filter: blur(10px);
  min-width: 70px;
  justify-content: space-between;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  &:hover {
    background: rgba(255, 255, 255, 1);
    border-color: rgba(0, 0, 0, 0.2);
    transform: translateY(-1px);
  }

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    padding: ${props => props.theme.spacing[1]} ${props => props.theme.spacing[2]};
    font-size: ${props => props.theme.typography.sizes.sm};
    min-width: 60px;
  }
`;

const Flag = styled.div<{ $language: Language }>`
  width: 20px;
  height: 15px;
  border-radius: 2px;
  background: ${props => {
    if (props.$language === 'fr') {
      return 'linear-gradient(to right, #002395 33%, white 33%, white 66%, #ED2939 66%)';
    } else {
      // Drapeau américain simplifié - 7 bandes rouges et 6 blanches
      return 'linear-gradient(to bottom, #B22234 0%, #B22234 7.7%, white 7.7%, white 15.4%, #B22234 15.4%, #B22234 23.1%, white 23.1%, white 30.8%, #B22234 30.8%, #B22234 38.5%, white 38.5%, white 46.2%, #B22234 46.2%, #B22234 53.8%, white 53.8%, white 61.5%, #B22234 61.5%, #B22234 69.2%, white 69.2%, white 76.9%, #B22234 76.9%, #B22234 84.6%, white 84.6%, white 92.3%, #B22234 92.3%, #B22234 100%)';
    }
  }};
  background-size: auto;
  position: relative;
  flex-shrink: 0;
  border: 0.5px solid rgba(0, 0, 0, 0.1);
  
  ${props => props.$language === 'en' && `
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 8px;
      height: 7px;
      background: #3C3B6E;
      border-radius: 1px 0 0 0;
    }
  `}
`;

const LanguageCode = styled.span`
  font-weight: ${props => props.theme.typography.weights.medium};
  font-size: ${props => props.theme.typography.sizes.sm};
  text-transform: uppercase;
  position: relative;
`;

const AutoDetectedIndicator = styled.span`
  position: absolute;
  top: -2px;
  right: -8px;
  width: 6px;
  height: 6px;
  background: ${props => props.theme.colors.success};
  border-radius: 50%;
  border: 1px solid white;
`;

const Chevron = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Dropdown = styled(motion.div)`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: ${props => props.theme.spacing[1]};
  background: rgba(255, 255, 255, 0.98);
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: ${props => props.theme.borderRadius.lg};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  backdrop-filter: blur(20px);
  overflow: hidden;
  z-index: 1000;
  min-width: 120px;
`;

const LanguageOption = styled(motion.button)`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
  width: 100%;
  padding: ${props => props.theme.spacing[2]} ${props => props.theme.spacing[3]};
  background: transparent;
  border: none;
  color: ${props => props.theme.colors.text};
  cursor: pointer;
  transition: background-color ${props => props.theme.animations.duration.fast} ${props => props.theme.animations.easing.smooth};
  font-size: ${props => props.theme.typography.sizes.sm};

  &:hover {
    background: ${props => props.theme.colors.primary[50]};
  }

  &:first-child {
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  }
`;

const LanguageSwitcher: React.FC = () => {
  const { language, setLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [isAutoDetected, setIsAutoDetected] = useState(false);

  // Vérifier si la langue a été détectée automatiquement
  React.useEffect(() => {
    const savedLanguage = localStorage.getItem('portfolio-language');
    setIsAutoDetected(!savedLanguage);
  }, []);

  const languages = [
    { code: 'en' as Language, name: 'English' },
    { code: 'fr' as Language, name: 'Français' },
  ];

  const handleLanguageChange = (newLanguage: Language) => {
    console.log('Changing language from', language, 'to', newLanguage);
    if (newLanguage !== language) {
      setLanguage(newLanguage);
    }
    setIsOpen(false);
  };

  // const currentLanguage = languages.find(lang => lang.code === language);

  return (
    <LanguageSwitcherContainer>
      <CurrentLanguage
        onClick={() => setIsOpen(!isOpen)}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
              <Flag $language={language} />
              <LanguageCode>
                {language.toUpperCase()}
                {isAutoDetected && <AutoDetectedIndicator title="Langue détectée automatiquement" />}
              </LanguageCode>
        <Chevron
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <ChevronDown size={14} />
        </Chevron>
      </CurrentLanguage>

      <AnimatePresence>
        {isOpen && (
          <Dropdown
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.15 }}
          >
            {languages.map((lang) => (
              <LanguageOption
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                whileHover={{ x: 4 }}
                whileTap={{ scale: 0.98 }}
              >
                <Flag $language={lang.code} />
                <span>{lang.name}</span>
              </LanguageOption>
            ))}
          </Dropdown>
        )}
      </AnimatePresence>
    </LanguageSwitcherContainer>
  );
};

export default LanguageSwitcher;
